import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { nightOwl } from "react-syntax-highlighter/dist/esm/styles/prism";

// ScrollProgress: Thin progress bar at the top indicating scroll percentage
const ScrollProgress = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handler = () => {
      const total = document.body.scrollHeight - window.innerHeight;
      setProgress((window.scrollY / total) * 100);
    };
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);
  return (
    <div className="fixed top-0 left-0 w-full h-1 bg-blue-200 z-50">
      <div
        className="h-full bg-blue-600 transition-all duration-200"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

// SectionCard: Animated card wrapper for each section (h3 + content)
// Added w-full md:w-1/2 so that on medium+ screens each card takes half width
const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    whileHover={{ scale: 1.03, y: -5 }}
    transition={{
      duration: 0.4,
      ease: "easeOut",
      type: "spring",
      stiffness: 200,
      damping: 20,
    }}
    viewport={{ once: true, amount: 0.2 }}
    className="w-full md:w-1/2 p-6 bg-white/80 backdrop-blur-sm shadow-lg border border-blue-100 rounded"
  >
    <h3 className="text-xl font-bold text-blue-900 mb-3">{title}</h3>
    {children}
  </motion.div>
);

// Paragraph: Styled paragraph for consistent text appearance
const Paragraph = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <p
    className={`text-lg text-gray-800 leading-relaxed text-justify ${className}`}
  >
    {children}
  </p>
);

// Solidity code snippet as a constant string
const transformXCode = `    function transformX(uint256 amountNAU, uint256 ratioX1, uint256 ratioX2) external {
        require(amountNAU > 0, "Controller: Amount must be > 0");
        require(ratioX1 + ratioX2 == 10000, "Controller: Ratios must total 10000");

        uint256 feeAmount = FullMath.mulDiv(amountNAU, FEE_BASIS_POINTS, BASIS_POINTS_DIVISOR);
        uint256 netAmount = amountNAU - feeAmount;

        IERC20(address(nau)).safeTransferFrom(msg.sender, address(this), amountNAU);
        IERC20(address(nau)).safeTransfer(reserveWallet, feeAmount);

        nau.controllerBurn(address(this), netAmount);

        uint256 priceNAU = getTwapPrice(address(nau));
        uint256 priceNAUY = getTwapPrice(address(nauy));
        uint256 priceNAUN = getTwapPrice(address(naun));

        uint256 totalValueInQuote = FullMath.mulDiv(netAmount, priceNAU, 1e18);

        uint256 valueNAUYInQuote = FullMath.mulDiv(totalValueInQuote, ratioX1, 10000);
        uint256 valueNAUNInQuote = totalValueInQuote - valueNAUYInQuote;

        require(priceNAUY > 0, "Controller: NAUY price is zero");
        require(priceNAUN > 0, "Controller: NAUN price is zero");
        uint256 amountNAUY = FullMath.mulDiv(valueNAUYInQuote, 1e18, priceNAUY);
        uint256 amountNAUN = FullMath.mulDiv(valueNAUNInQuote, 1e18, priceNAUN);

        nauy.controllerMint(msg.sender, amountNAUY);
        naun.controllerMint(msg.sender, amountNAUN);

        emit Transformed(msg.sender, amountNAU, amountNAUY, amountNAUN);
}`;

// Main component for FairBurn
const FairBurn = () => (
  <div className="relative overflow-hidden">
    {/* Render scroll progress bar and background animation */}
    <ScrollProgress />

    <div className="space-y-8 px-4 pb-10 md:py-6 md:px-8">
      {/* Intro: static heading and explanatory paragraph */}
      <div className="max-w-6xl mx-auto space-y-4 text-center">
        <h2 className="text-3xl font-bold text-primary-dark">
          Fair Burn-and-Mint Mechanism &amp; Arbitrage Prevention in UcWaves
        </h2>
        <Paragraph className="text-gray-700 max-w-6xl mx-auto text-justify">
          The Uncensored Waves (UcWaves) Controller contract implements a
          trustless, fair, and manipulation-resistant mechanism for burning base
          tokens (NAU) and minting opinion tokens (NAUY and NAUN). Key design
          elements—including a 1% fee, time-weighted average price (TWAP)
          oracles, ratio constraints, and atomic execution—combine to prevent
          arbitrage and ensure equitable treatment of all participants. The
          following explains how the system works, why it is fair, and why
          arbitrageurs cannot exploit the burn-and-mint flow.
        </Paragraph>
      </div>

      {/* Two-column layout for the main sections */}
      <div className="flex flex-col md:flex-row gap-6 md:gap-8 max-w-6xl mx-auto">
        {/* Section 1: Core Transform Function Overview */}
        <SectionCard title="1. Core Transform Function Overview">
          <div className="relative overflow-x-auto mb-4">
            <SyntaxHighlighter
              language="solidity"
              style={nightOwl}
              customStyle={{
                padding: "1rem",
                minWidth: "400px",
                fontSize: "8px",
                background: "#0d1117",
              }}
            >
              {transformXCode}
            </SyntaxHighlighter>
          </div>
          <Paragraph>Key steps:</Paragraph>
          <ul className="list-disc list-inside text-base text-gray-800 mb-4">
            <li>
              <strong>Fee deduction:</strong> 1% of NAU burned is routed to a
              reserve wallet.
            </li>
            <br />
            <li>
              <strong>Burn:</strong> Remaining NAU is permanently removed via{" "}
              <code>nau.burn()</code>.
            </li>
            <br />
            <li>
              <strong>Price discovery:</strong> TWAP oracles (getTwapPrice)
              fetch smoothed prices for NAU, NAUY, and NAUN.
            </li>
            <br />
            <li>
              <strong>Value computation:</strong> Net NAU value in quote tokens
              is split by user-defined ratios that sum to 100%.
            </li>
            <br />
            <li>
              <strong>Mint:</strong> Opinion tokens are minted according to
              computed values.
            </li>
          </ul>
          <Paragraph>
            This atomic sequence ensures users always receive the correct amount
            of opinion tokens or the transaction reverts.
          </Paragraph>
        </SectionCard>

        {/* Section 2: Fairness Through Fee and Ratio Constraints */}
        <SectionCard title="2. Fairness Through Fee and Ratio Constraints">
          <Paragraph>
            <span className="font-bold text-primary-dark">
              2.1 1% Fee Mechanism
            </span>
          </Paragraph>
          <ul className="list-disc list-inside text-base text-gray-800 mb-4">
            <li>Uniform levy: Every burn operation incurs a fixed 1% fee.</li>
            <br />
            <li>
              Purpose: Fees fund development and community incentives, and
              discourage tiny arbitrage loops.
            </li>
            <br />
            <li>
              Predictable cost: Basis-point–driven fee ensures exact known cost
              upfront.
            </li>
          </ul>

          <Paragraph>
            <span className="font-bold text-primary-dark">
              2.2 Ratio Enforcement
            </span>
          </Paragraph>
          <ul className="list-disc list-inside text-base text-gray-800 mb-4">
            <li>
              User sovereignty: Callers specify value split via{" "}
              <code>ratioX1</code> and <code>ratioX2</code>.
            </li>
            <br />
            <li>
              Validation: <code>require(ratioX1 + ratioX2 == 10000)</code>{" "}
              enforces full 100% split.
            </li>
            <br />
            <li>
              Immutable splitting: Guarantees no hidden dilution beyond net
              value.
            </li>
          </ul>
        </SectionCard>
      </div>
    </div>
    <Paragraph className="text-gray-700 max-w-6xl mx-auto text-justify pl-6">
      Together, these constraints mean every user pays the same fee and obtains
      tokens in exact proportion, with no privileged paths or discounts.
    </Paragraph>
  </div>
);

export default FairBurn;
